/**
 * Halloween avatars — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenAvatarsPack: ToilePack = {
    slug: 'halloween-avatars',
    category: 'season',
    name: 'Halloween avatars',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'ghost', label: 'Ghost avatar', subject: 'A little ghost face with rosy cheeks inside a round frame, built to read small, isolated'},
        {key: 'pumpkin', label: 'Pumpkin avatar', subject: 'A carved pumpkin face inside a round frame, built to read small, isolated'},
        {key: 'cat', label: 'Cat avatar', subject: 'A black cat face with green eyes inside a round frame, built to read small, isolated'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['as a matching pair', 'with sunglasses', 'for a group chat set'],
    directionHint: 'try: as a matching pair',
    seo: {
        title: 'Halloween Avatars: Ghost, Pumpkin, Cat Designs',
        metaDescription: 'Nine halloween avatars -- ghost, pumpkin, cat -- in spooky cute, vintage, and bold graphic finishes. Round, built to read small.',
        h1: 'Halloween profile picture: ghost, pumpkin, and cat avatars',
        tagline: 'Nine faces, three October characters, three finishes each.',
        intro: 'Nine round halloween avatar designs: a ghost face, a carved pumpkin face, and a black cat face, each built to read small inside a round frame. Three finishes -- spooky cute, vintage, and bold graphic -- for a profile picture on any platform. Ask for sunglasses instead, or a group chat set, and the same three finishes carry it.',
        useCases: [
            'a social media profile picture',
            'a Discord or gaming avatar',
            'a group chat icon set',
            'a halloween event page icon',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine round avatar designs -- a ghost, a pumpkin, and a cat -- each in spooky cute, vintage, and bold graphic finishes, built to read clean at small size.'},
            {q: 'Does this come as a circle already?', a: 'Yes -- each avatar is composed inside a round frame, ready to drop straight into a profile picture slot.'},
            {q: 'Can I get a different character, like with sunglasses?', a: 'Yes -- the direction field takes any variation, including with sunglasses, as a matching pair, or for a group chat set.'},
            {q: 'Can I use this for a business account?', a: 'Yes -- the avatars are yours to use for personal or business profiles across any platform.'},
        ],
    },
}
